<template>
  <v-card flat id="generator-tab-container" height="100%">
      <v-card-title
        v-if="!hideResearchQuestionInput"
        class="title"
        :class="addOverlay ? 'opaque-overlay' : ''"
      >
        Research Question
      </v-card-title>
      <v-card-text class="subtitle">
        Use AI to convert your research question into a systematic search. <a :href="blogAIGenUrl" target="_blank">More details <v-icon class="external-link-icon">open_in_new</v-icon></a>
      </v-card-text>
      <v-card-actions
        v-if="!hideResearchQuestionInput"
        id="research-question-input-container"
        :class="addOverlay ? 'opaque-overlay' : ''">
        <v-text-field
          v-model="researchQuestion"
          placeholder="The effects of physical activity on obesity in older people"
          id="research-question-input"
          @click="rqInputShowErrorMessage=false"
          @keyup.enter="processResearchQuestion"
        ></v-text-field>
        <span :class="rqInputShowErrorMessage ? 'error-msg-active' : 'error-msg'">
          {{ rqInputShowErrorMessage ? 'No concepts detected, please try another research question' : ''}}
        </span>
      </v-card-actions>
      <v-card-actions
        v-if="hideResearchQuestionInput"
        id="research-question-presented-container"
        :class="addOverlay ? 'opaque-overlay' : ''">
        <v-card-text class="title">
          <span style="font-weight: bold">Q:</span>
          <!--  @todo - add animated text to AI text  -->
          <!--  <animated-text text="Use this component to animate response throughout the generator"></animated-text>  -->
          {{ researchQuestion }}
        </v-card-text>
      </v-card-actions>
      <div v-if="displayKeyConcept" class="key-concepts-container" :class="addOverlay ? 'opaque-overlay' : ''">
        <v-card-title class="title">Select your Key Concepts</v-card-title>
        <v-divider></v-divider>
        <v-card-text id="key-concept-phrase">
          <span
            :class="!term.isFacet ? 'non-facet-p' : ''"
            v-for="[index, term] of researchQuestionProcessed.entries()"
            :key="index"
          >
            <span
              v-if="term.isFacet"
              :class="applyKeyConceptPillColorClass(term.isSelected)"
              @click="editKeyConcepts ? toggleKeyConceptPill(term.phrase) : null"
            >
              <v-avatar v-if="editKeyConcepts" left class="key-concept-avatar">
                <v-icon v-if="term.isSelected" size="18px" color="white">check_box</v-icon>
                <v-icon v-if="!term.isSelected" size="18px" color="grey darken-2">check_box_outline_blank</v-icon>
              </v-avatar>
              {{ term.phrase }}
            </span>
            <span v-else class="non-facet-term">{{ term.phrase }}</span>
          </span>
        </v-card-text>
      </div>
      <div v-if="displaySelectKeyterms" id="relevant-keyterms-title" :class="addOverlay ? 'opaque-overlay relevant-keyterms-container-overlay' : ''">
        <v-card-title class="title">Select your Keywords</v-card-title>
        <v-divider></v-divider>
      </div>
      <div v-if="displaySelectKeyterms" id="relevant-keyterms-container" :class="addOverlay ? 'opaque-overlay' : ''">
        <div
          v-if="!displayRelevantKeyterms"
          id="relevant-keyterms-loading"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            :size="36"></v-progress-circular>
        </div>
        <div v-if="rateLimitHit" id="rate-limit-msg">{{ rateLimitMessage }}</div>
        <v-card-text
          v-if="displayRelevantKeyterms"
          class="selected-keyword-textContainer"
        >
          <div
            v-for="(termObject, term, index) in relevantKeyterms"
            :key="index"
          >
            <div class="selected-keyword-header-container">
              <span class="selected-keyword-header">
                <v-avatar left @click="editKeyterms ? toggleSuggestedTermGroup(term) : null">
                  <v-icon v-if="termObject.selectedState === 'partial'" size="18px" color="white">indeterminate_check_box</v-icon>
                  <v-icon v-else-if="termObject.selectedState === 'unchecked'" size="18px" color="white">check_box_outline_blank</v-icon>
                  <v-icon v-else size="18px" color="white">check_box</v-icon>
                </v-avatar>
                {{ term }}<br>
              </span>
            </div>
            <div class="selected-keyword-body">
              <div
                v-for="(isSelected, suggestedTerm, index) of termObject.terms"
                :key="index"
              >
                <span
                  class="selected-keywords-chip"
                  @click="editKeyterms ? toggleSuggestedTermPill(term, suggestedTerm) : null"
                >
                  <v-avatar left class="selected-keyword-avatar">
                    <v-icon v-if="isSelected" size="18px" color="blue-grey darken-2">check_box</v-icon>
                    <v-icon v-else size="18px" color="blue-grey darken-2">check_box_outline_blank</v-icon>
                  </v-avatar>
                  {{ suggestedTerm }}
                </span>
              </div>
            </div>
          </div>
        </v-card-text>
      </div>

    <v-card-actions id="task-tip-container" :class="currentStage==='generated' ? 'stick-bottom' : ''">
      <div>
        <v-card-title class="title" id="task-tip-title">Task Tip: {{ taskTipTitle }}</v-card-title>
        <v-card-text id="task-tip-subtitle">{{ taskTipSubtitle }}</v-card-text>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        v-if="currentStage!=='initial'"
        icon depressed
        @click.stop="handleTaskTipBack"
        id="task-tip-back-btn"
      >
        <v-icon id="task-tip-back-btn-icon">arrow_back</v-icon>
      </v-btn>
      <v-btn
        v-if="currentStage==='generated'"
        large
        color="secondary"
        id="task-tip-second-btn"
        @click.stop="handleTaskTipSecondBtn"
      >
        {{ taskTipSecondButton }}
      </v-btn>
      <v-btn
        large
        id="task-tip-main-btn"
        @click.stop="handleTaskTipMainBtn"
        :disabled="!researchQuestion.length>0 || !enableMainBtn"
      >{{ taskTipMainButton }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { getResearchQuestionPhraseExtraction, getRelevantKeywords } from '../../utils/nlp-api-v2'
import { blogAIGenUrl } from '../../utils/constants'
import '../../style/_variables.scss'

export default {
  name: 'SearchAI',
  data () {
    return {
      currentStage: 'initial',
      displayKeyConcept: false,
      editKeyConcepts: true,
      displaySelectKeyterms: false,
      displayRelevantKeyterms: false,
      rateLimitHit: false,
      rateLimitMessage: 'Rate limit hit. We recommend waiting a few seconds and trying again.',
      editKeyterms: true,
      hideResearchQuestionInput: false,
      researchQuestion: '',
      rqInputShowErrorMessage: false,
      researchQuestionProcessed: [],
      selectedFacets: [],
      relevantKeyterms: {},
      generatorFinalSearchQuery: '',
      addOverlay: false,
      taskTipTitle: '',
      taskTipSubtitle: '',
      taskTipMainButton: '',
      enableMainBtn: true,
      dynamicText: {
        taskTipTitle: {
          initial: 'Enter your research question',
          keyConcepts: 'Deselect unwanted concepts',
          selectedKeyterms: 'Select suggested keywords',
          generated: 'Congratulations, your search has been generated on the canvas'
        },
        taskTipSubtitle: {
          initial: 'Type your research question as a sentence, then click GO.',
          keyConcepts: 'Select each concept to toggle on or off, then click NEXT.',
          selectedKeyterms: 'Select keywords to include in your search, then click GENERATE to populate the canvas.',
          generated: 'You can view your search results, start again, or go back to make changes.'
        },
        taskTipMainButton: {
          initial: 'Go',
          keyConcepts: 'Next',
          selectedKeyterms: 'Generate',
          generated: 'Show Results'
        },
        taskTipSecondButton: {
          generated: 'Restart'
        }
      },
      blogAIGenUrl: blogAIGenUrl,
      verbose: false
    }
  },
  created () {
    this.updateDynamicText('initial')
  },
  methods: {
    processResearchQuestion () {
      this.researchQuestion = this.sanitiseString(this.researchQuestion.toLowerCase())
      if (this.verbose) { console.log('sanitisedInput:', this.researchQuestion) }
      if (this.researchQuestion.length > 0) {
        getResearchQuestionPhraseExtraction(this.researchQuestion, '&method=textacy-np')
          .then(response => {
            if (this.verbose) { console.log('API-response:', response) }
            const responseArray = this.sortResponseArray(response.response)
            if (this.verbose) { console.log('sortedResponse:', responseArray) }
            let nonFacetQuery = this.researchQuestion
            for (let phrase of responseArray) {
              const splitString = nonFacetQuery.split(phrase)
              if (splitString.length === 2) {
                nonFacetQuery = splitString.join()
              } else if (splitString.length > 2) {
                const [first, ...rest] = nonFacetQuery.split(phrase) // Makes sure to take 1st occurrence of phrase only
                nonFacetQuery = first + ',' + rest.join(phrase)
              }
            }
            let nonFacetQueryArray = nonFacetQuery.split(',').filter(el => el !== '')
            if (this.verbose) { console.log('nonFacetQueryArray:', nonFacetQueryArray) }

            const loopNumber = Math.max(responseArray.length, nonFacetQueryArray.length)
            let tempResearchQuestion = this.researchQuestion
            if (this.verbose) { console.log('loopNumber:', loopNumber) }
            for (let i = 0; i < loopNumber; i++) {
              // @todo - regex of .search() is not case insensitive -> can't seem to add 'i' flag required
              let indexFacet = tempResearchQuestion.search(responseArray[i])
              let indexNonFacet = tempResearchQuestion.search(nonFacetQueryArray[i])
              if (indexFacet === -1 || indexNonFacet === -1) {
                if (this.verbose) { console.log('skippedLoop-indexFacet:', indexFacet, ', indexNonFacet:', indexNonFacet) }
                continue
              }
              if (responseArray[i] === undefined || indexFacet < 0) {
                if (this.verbose) { console.log('AddedNonFacet:', nonFacetQueryArray[i]) }
                tempResearchQuestion = tempResearchQuestion.replace(nonFacetQuery[i], '')
                this.researchQuestionProcessed.push({ phrase: nonFacetQueryArray[i], isFacet: false, isSelected: false })
              } else if (nonFacetQueryArray[i] === undefined) {
                if (this.verbose) { console.log('AddedFacet:', responseArray[i]) }
                tempResearchQuestion = tempResearchQuestion.replace(responseArray[i], '')
                this.researchQuestionProcessed.push({ phrase: responseArray[i], isFacet: true, isSelected: true })
              } else if (indexFacet < indexNonFacet) {
                if (this.verbose) { console.log('reordered-Facets:', responseArray[i], ' + nonFacet:', nonFacetQueryArray[i]) }
                tempResearchQuestion = tempResearchQuestion.replace(responseArray[i], '')
                tempResearchQuestion = tempResearchQuestion.replace(nonFacetQueryArray[i], '')
                this.researchQuestionProcessed.push({ phrase: responseArray[i], isFacet: true, isSelected: true })
                this.researchQuestionProcessed.push({ phrase: nonFacetQueryArray[i], isFacet: false, isSelected: false })
              } else {
                if (this.verbose) { console.log('reordered-NonFacets:', nonFacetQueryArray[i], ' + Facet:', responseArray[i]) }
                tempResearchQuestion = tempResearchQuestion.replace(nonFacetQueryArray[i], '')
                tempResearchQuestion = tempResearchQuestion.replace(responseArray[i], '')
                this.researchQuestionProcessed.push({ phrase: nonFacetQueryArray[i], isFacet: false, isSelected: false })
                this.researchQuestionProcessed.push({ phrase: responseArray[i], isFacet: true, isSelected: true })
              }
            }
            if (this.verbose) { console.log('researchQuestionProcessed:', this.researchQuestionProcessed) }
          })
          .then(_ => {
            if (this.getSelectedFacets().length === 0) {
              this.rqInputShowErrorMessage = true
            } else {
              this.rqInputShowErrorMessage = false
              this.enableMainBtn = this.getSelectedFacets().length !== 0
              this.hideResearchQuestionInput = true
              this.displayKeyConcept = true
              this.currentStage = 'keyConcepts'
              this.updateDynamicText('keyConcepts')
            }
          })
      }
    },
    sanitiseString (string) {
      const charToRemove = ['?', '*']
      let result = string
      for (const el of charToRemove) {
        while (result.includes(el)) {
          result = result.replace(el, '')
        }
      }
      return result
    },
    sortResponseArray (response) {
      return response.sort(this.comparePositionInRQ)
    },
    comparePositionInRQ (a, b) {
      if (this.researchQuestion.search(a) < this.researchQuestion.search(b)) {
        return -1
      } else if (this.researchQuestion.search(a) > this.researchQuestion.search(b)) {
        return 1
      }
      return 0
    },
    processRelevantKeyterms () {
      this.relevantKeyterms = {}
      this.selectedFacets = this.getSelectedFacets()
      if (this.selectedFacets.length > 0) {
        const makeRequest = (facetArray) => {
          if (facetArray.length > 0) {
            setTimeout(() => {
              getRelevantKeywords(facetArray[0].phrase, this.researchQuestion === '' ? undefined : this.researchQuestion)
                .then(response => {
                  if (response.response === -1) {
                    this.rateLimitHit = true
                  } else {
                    this.rateLimitHit = false
                    // eslint-disable-next-line
                    let processedResponse = response.response.reduce((acc, curr) => (acc[curr] = false, acc), {})
                    processedResponse = Object.assign({[facetArray[0].phrase]: true}, processedResponse)
                    Object.assign(this.relevantKeyterms, {[facetArray[0].phrase]: { 'terms': processedResponse, 'selectedState': 'partial' }})
                    this.$forceUpdate()
                  }
                })
            }, 1500)
            setTimeout(() => makeRequest(facetArray.slice(1)), 1500)
          } else {
            this.displayRelevantKeyterms = true
          }
        }
        makeRequest(this.selectedFacets)
      }
    },
    generateSearchQuery () {
      let queryString = ''
      Object.entries(this.relevantKeyterms).forEach(([mainFacet, termObject], index) => {
        queryString = queryString.concat('', '(')
        Object.entries(termObject.terms).forEach(([keyterm, isSelected], index) => {
          if (isSelected) { queryString = queryString.concat('', index === 0 ? keyterm : ` OR '${keyterm}'`) }
        })
        if (index + 1 === Object.entries(this.relevantKeyterms).length) {
          queryString = queryString.concat('', ')')
        } else {
          queryString = queryString.concat('', ') AND ')
        }
      })
      this.generatorFinalSearchQuery = queryString
      this.$emit('passFinalQuery', [queryString, 'polyglot'])
    },
    handleTaskTipMainBtn () {
      switch (this.currentStage) {
        case 'initial':
          this.processResearchQuestion()
          break
        case 'keyConcepts':
          this.processRelevantKeyterms()
          this.updateDynamicText('selectedKeyterms')
          this.setStage('selectedKeyterms')
          break
        case 'selectedKeyterms':
          this.generateSearchQuery()
          this.updateDynamicText('generated')
          this.setStage('generated')
          break
        case 'generated':
          this.$store.commit('showResultsComponent')
          break
      }
    },
    handleTaskTipBack () {
      switch (this.currentStage) {
        case 'keyConcepts':
          this.updateDynamicText('initial')
          this.resetGenerator(true)
          break
        case 'selectedKeyterms':
          this.updateDynamicText('keyConcepts')
          this.setStage('keyConcepts')
          break
        case 'generated':
          this.updateDynamicText('selectedKeyterms')
          this.setStage('selectedKeyterms')
          break
      }
    },
    handleTaskTipSecondBtn () {
      switch (this.currentStage) {
        case 'generated':
          this.resetGenerator()
          break
      }
    },
    setStage (nextStage) {
      switch (nextStage) {
        case 'initial':
        default:
          this.currentStage = 'initial'
          this.addOverlay = false
          this.displaySelectKeyterms = false
          this.displayRelevantKeyterms = false
          this.editKeyConcepts = true
          this.editKeyterms = true
          this.enableMainBtn = true
          break
        case 'keyConcepts':
          this.currentStage = 'keyConcepts'
          this.addOverlay = false
          this.displaySelectKeyterms = false
          this.displayRelevantKeyterms = false
          this.hideResearchQuestionInput = true
          this.displayKeyConcept = true
          this.editKeyConcepts = true
          this.editKeyterms = true
          break
        case 'selectedKeyterms':
          this.currentStage = 'selectedKeyterms'
          this.addOverlay = false
          this.displaySelectKeyterms = true
          this.editKeyConcepts = false
          this.editKeyterms = true
          break
        case 'generated':
          this.currentStage = 'generated'
          this.addOverlay = true
          this.editKeyterms = false
          break
      }
    },
    updateDynamicText (nextStage) {
      for (const [key, value] of Object.entries(this.dynamicText)) {
        this[key] = value[nextStage]
      }
    },
    toggleKeyConceptPill (phrase) {
      const currentEntry = this.researchQuestionProcessed.find(function (obj) { return obj.phrase === phrase })
      let modifiedEntry = currentEntry
      modifiedEntry.isSelected = !currentEntry.isSelected
      Object.assign(currentEntry, modifiedEntry)
      this.$forceUpdate()
    },
    toggleSuggestedTermGroup (phrase) {
      switch (this.relevantKeyterms[phrase].selectedState) {
        case 'unchecked':
        case 'partial':
          this.relevantKeyterms[phrase].selectedState = 'checked'
          this.updateAllRelevantKeyterms(phrase, true)
          this.$forceUpdate()
          break
        case 'checked':
          this.relevantKeyterms[phrase].selectedState = 'unchecked'
          this.updateAllRelevantKeyterms(phrase, false)
          this.$forceUpdate()
          break
      }
    },
    updateAllRelevantKeyterms (facetTerm, value) {
      Object.entries(this.relevantKeyterms[facetTerm].terms).forEach(([term, _]) => {
        this.relevantKeyterms[facetTerm].terms[term] = value
      })
    },
    toggleSuggestedTermPill (phrase, suggestedTerm) {
      if (this.relevantKeyterms[phrase].selectedState === 'unchecked' || this.relevantKeyterms[phrase].selectedState === 'checked') {
        this.relevantKeyterms[phrase].selectedState = 'partial'
      }
      this.relevantKeyterms[phrase].terms[suggestedTerm] = !this.relevantKeyterms[phrase].terms[suggestedTerm]
      const allTermsSelected = Object.values(this.relevantKeyterms[phrase].terms).filter((term) => term === false).length === 0
      const noTermsSelected = Object.values(this.relevantKeyterms[phrase].terms).filter((term) => term === true).length === 0
      if (allTermsSelected) { this.relevantKeyterms[phrase].selectedState = 'checked' }
      if (noTermsSelected) { this.relevantKeyterms[phrase].selectedState = 'unchecked' }
      this.$forceUpdate()
    },
    applyKeyConceptPillColorClass (isSelected) {
      switch (isSelected) {
        case true:
          return this.editKeyConcepts ? 'key-concept-chip key-concept-chip-selected' : 'key-concept-chip key-concept-chip-selected opacity-50'
        case false:
          return this.editKeyConcepts ? 'key-concept-chip' : 'key-concept-chip opacity-50'
      }
    },
    resetGenerator (keepRQ = false) {
      this.currentStage = 'initial'
      this.displayKeyConcept = false
      this.editKeyConcepts = true
      this.displaySelectKeyterms = false
      this.displayRelevantKeyterms = false
      this.editKeyterms = true
      this.enableMainBtn = true
      this.hideResearchQuestionInput = false
      if (!keepRQ) { this.researchQuestion = '' }
      this.researchQuestionProcessed = []
      this.selectedFacets = []
      this.relevantKeyterms = {}
      this.generatorFinalSearchQuery = ''
      this.addOverlay = false
      this.updateDynamicText('initial')
    },
    getSelectedFacets () {
      return this.researchQuestionProcessed.filter(function (obj) { return obj.isFacet === true && obj.isSelected === true })
    }
  }
}
</script>

<style lang="scss" scoped>
.card__title {
  padding-left: 25px;
  padding-top: 0;
  padding-bottom: 0;
  color: $blue;
  font-weight: bold;
}
.external-link-icon {
  font-size: 16px;
}
.subtitle {
  padding-left: 25px;
}
.card__actions {
  padding-top: 0;
  margin-top: 0;
}
#generator-tab-container {
  height: 100vh;
  margin-top: 8px;
  padding: 0;
  display: flex;
  flex-direction: column;
}
#research-question-input-container {
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: baseline;
}
#research-question-input {
  width: 80%;
  position: relative;
  padding-left: 15px;
}
#research-question-presented-container {
  flex: 0 0 60px;
}
.error-msg, .error-msg-active {
  color: $orange;
  height: 21px;
  width: 100%;
  margin-top: -25px;
  padding-top: 5px;
  &-active {
    border-top: 2px solid $orange;
  }
}
.key-concepts-container {
  flex: 0 0 90px;
}
#key-concept-phrase {
  display: flex;
  flex-wrap: wrap;
  margin-left: 10px;
}
.key-concept-chip, .selected-keywords-chip {
  border-radius: .2rem;
  border: .6mm solid $grey-400;
  margin: 0.5rem;
  padding: 0.3rem;
  color: $blue-grey-600;
  display: flex;
  align-items: center;
  height: 2.5em;
}
.key-concept-chip-selected {
  font-weight: 600;
  border-color: $orange-200;
  background-color: $orange-200;
  color: $grey-50;
}
.opacity-50 {
  opacity: 0.5;
}
.key-concept-avatar, .selected-keyword-avatar {
  margin-left: -15px;
  margin-right: -10px
}
.key-concept-chip, .key-concept-avatar, .selected-keyword-avatar {
  transition: all 1s;
}
.chip--active:not(.chip--disabled):after, .chip--selected:not(.chip--disabled):after, .chip:focus:not(.chip--disabled):after {
  background-color: transparent;
}
.facet-p {
  margin-top: .85rem;
}
.non-facet-p {
  margin-top: .85rem;
  margin-left: .25rem;
}
#relevant-keyterms-title {
  flex: 0 0 30px;
}
/* FORCE scrollbar to always appear */
::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 7px;
}
/* FORCE scrollbar to always appear */
::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, .5);
  -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, .5);
}
#relevant-keyterms-container {
  /*flex: 60 2 0;*/
  overflow-y: scroll;
}
.relevant-keyterms-container-overlay::after {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: $grey-600;
}
.selected-keyword-textContainer {
  padding-top: .5rem;
}
.selected-keyword-header-container {
  width: 100%;
  height: 30px;
  overflow: hidden;
  background-color: $blue-grey-300;
}
.selected-keyword-header {
  color: $grey-50;
  font-weight: 600;
  position: relative;
  top: -7px;
  left: -15px;
  padding: .5rem;
  text-transform: capitalize;
  margin: .5rem 0;
}
.selected-keyword-body {
  display: flex;
  flex-wrap: wrap;
}
#rate-limit-msg {
  display: flex;
  justify-content: center;
  font-size: 16px;
  color: $orange-800;
}
#relevant-keyterms-loading {
  padding: 15px;
  display: flex;
  justify-content: center;
  align-content: center;
}
#task-tip-container {
  width: 100%;
  background-color: $blue-grey-600;
  border-top: 1px solid rgba(0,0,0,.12);
  padding: .75rem;
  z-index: 20;
}
#task-tip-title {
  margin-top: 8px;
  padding-bottom: 1px;
  padding-left: 15px;
  color: $orange-100;
  font-size: 16px;
}
#task-tip-subtitle {
  padding-top: 2px;
  color: $grey-50;
  font-weight: 600;
  font-size: 14px;
}
#task-tip-back-btn {
  z-index: 10;
}
#task-tip-back-btn-icon {
  color: $grey-50;
}
#task-tip-main-btn {
  min-width: 9em;
  background-color: $grey-50;
  color: $blue-grey-300;
}
.stick-bottom {
  position: absolute;
  bottom: 0;
}
.opaque-overlay {
  background-color: $grey-600;
  opacity: .3;
  z-index: 10;
}
</style>



// WEBPACK FOOTER //
// src/components/parts/SearchAI.vue